import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import { setApiToken } from '../utils/apiClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setTokenState] = useState(null); // In-memory only — never persisted to localStorage
  // Wrap setToken so the centralized apiClient always has the latest token
  // for its Bearer-injection interceptor.
  const setToken = useCallback((next) => {
    setApiToken(next);
    setTokenState(next);
  }, []);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // On mount: restore session from httpOnly cookie via /auth/me
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    restoreSession();
  }, []);

  // Restore session from httpOnly cookie
  const restoreSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        // Restore JWT in memory so WebSockets (lobby / room / private chat)
        // can authenticate via Bearer on their first frame after a reload.
        if (data.token) {
          setToken(data.token);
        }
      }
    } catch (error) {
      logger.warn('Session restore failed:', error.message);
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch user when token changes (for in-session Bearer auth)
  useEffect(() => {
    if (!token || user) return;
    const fetchUser = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
        }
      } catch (error) {
        logger.warn('Token refresh failed:', error.message);
      }
    };
    fetchUser();
  }, [token, API_URL, user]);

  useEffect(() => {
    const handleAvatarUpdate = (event) => {
      if (user && event.detail?.avatar) {
        setUser(prev => ({ ...prev, avatar: event.detail.avatar }));
      }
    };
    window.addEventListener('avatarUpdated', handleAvatarUpdate);
    return () => window.removeEventListener('avatarUpdated', handleAvatarUpdate);
  }, [user]);

  const register = async (email, password, name, securityQuestions = null, termsData = null) => {
    try {
      const requestBody = { email, password, name };
      if (securityQuestions) {
        requestBody.security_question_1 = securityQuestions.question1;
        requestBody.security_answer_1 = securityQuestions.answer1;
        requestBody.security_question_2 = securityQuestions.question2;
        requestBody.security_answer_2 = securityQuestions.answer2;
      }
      if (termsData) {
        requestBody.terms_accepted = termsData.terms_accepted;
        requestBody.terms_version = termsData.terms_version;
        requestBody.terms_hash = termsData.terms_hash;
      }

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok) {
        const authToken = data.token || data.authToken || data.access_token;
        if (!authToken) {
          return { success: false, error: 'No authentication token received' };
        }
        setToken(authToken);  // In-memory only — httpOnly cookie set by backend
        setUser(data.user);
        return { success: true };
      } else {
        return { success: false, error: data.detail || 'Registration failed' };
      }
    } catch (error) {
      return { success: false, error: `Network error: ${error.message}` };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        const authToken = data.token || data.authToken || data.access_token;
        if (!authToken) {
          return { success: false, error: 'No authentication token received' };
        }
        setToken(authToken);  // In-memory only — httpOnly cookie set by backend
        setUser(data.user);
        return { success: true };
      } else {
        return { success: false, error: data.detail || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginAsGuest = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/guest`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setToken(data.token);
        setUser(data.user);
        return { success: true };
      }
      return { success: false, error: data.detail || 'Guest login failed' };
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const processGoogleCallback = useCallback(async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/google/session-data`, {
        method: 'POST',
        headers: { 'X-Session-ID': sessionId, 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setUser(data.user);
        const meResponse = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (meResponse.ok) {
          const meData = await meResponse.json();
          setUser(meData.user);
        }
        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.detail || 'Google login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }, [API_URL]);

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      logger.warn('Logout request failed:', error.message);
    }
    setUser(null);
    setToken(null);
  };

  const value = {
    user,
    token,
    loading,
    register,
    login,
    loginWithGoogle,
    loginAsGuest,
    processGoogleCallback,
    logout,
    setUser,
    setToken,
    setIsAuthenticated: (val) => {},
    isAuthenticated: !!user,
    isGuest: user?.is_guest || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
